import type { ApiValidateConflictsPayload } from '@/features/bidding/types/api'
import type { ExtendedBid, OfferedAndForecastAndSuggestedPtu } from '@/features/bidding/types/bid'

/**
 * Builds a validation payload using the current (user-edited) PTU values.
 *
 * This function merges bid context (portfolio, deliveryDay, etc.) from the bids
 * with the current PTU values from the form state, ensuring validation always
 * uses the latest user edits rather than stale cached data.
 *
 * @param bids - The base bids containing context (portfolio, deliveryDay, etc.)
 * @param currentPtusByActivationGroup - Map of current (edited) PTUs by activation group UUID
 * @returns Validation payload with current PTU values
 */
export function buildValidationPayloadFromCurrentPtus(
  bids: ExtendedBid[],
  currentPtusByActivationGroup: Map<string, OfferedAndForecastAndSuggestedPtu[]>,
): ApiValidateConflictsPayload {
  return bids.map((bid) => {
    // Get the current PTU values for this bid's activation group
    const currentPtus = currentPtusByActivationGroup.get(bid.activationGroupUuid) ?? bid.ptus

    return {
      context: {
        portfolio: bid.portfolio,
        deliveryDay: bid.deliveryDay.toISODate(),
        marketProgram: bid.marketProgram,
        activationGroupUuid: bid.activationGroupUuid,
        bidType: bid.bidType,
      },
      offeredValues: currentPtus.map((ptu) => ({
        ptu: ptu.ptu,
        volume: ptu.offeredVolume,
        ptuChunks: ptu.ptuChunks,
      })),
      status: bid.status,
    }
  })
}
